import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChefHat, Shield, ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  const sections = [
    {
      title: '1. Introduction',
      content: `RecipeUp ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our recipe management and meal planning software for Windows ("RecipeUp" or "the Software").

We believe in complete transparency about our data practices. RecipeUp is designed to be privacy-first, operating entirely offline for its core functions with no data collection. This policy describes the limited circumstances where data may be processed and how we protect your privacy.

By downloading, installing, or using RecipeUp, you agree to the collection and use of information in accordance with this policy. If you do not agree with the terms of this Privacy Policy, please do not download, install, or use the Software.`
    },
    {
      title: '2. Information We Collect',
      content: `RecipeUp is designed with privacy as a core principle. Here's a comprehensive breakdown of our data practices:

Information We DO NOT Collect:
• Personal identification information (name, email address, phone number, physical address) — unless you voluntarily provide it when contacting support
• Your recipes, meal plans, dietary preferences, or nutrition data
• Your shopping lists, ingredient inventories, or food purchase history
• Browsing history, search history, or recipe browsing patterns
• Location data, GPS coordinates, or geographic information
• Device identifiers, advertising IDs, or tracking cookies
• Your contacts, calendar, photos, or other personal data
• Keystroke data, screenshots, or screen recordings
• Financial information or payment details
• Health or medical information

Information We MAY Collect (Only With Your Explicit Consent):
• Crash reports and diagnostic data — to help us identify and fix bugs
• Anonymous, aggregated usage statistics — such as which features are most popular
• Windows version and hardware configuration — for compatibility purposes
• App version information — to ensure you have the latest updates

Information You May Voluntarily Provide:
• Contact information when reaching out to our support team
• Feedback, suggestions, or bug reports you choose to submit
• Any information you include in communications with us`
    },
    {
      title: '3. How Your Data Is Processed',
      content: `Your Data Privacy is Our Top Priority:

Local Processing Only:
• All recipe management, meal planning, and nutrition tracking happen 100% locally on your Windows PC
• Your recipes and meal data are NEVER uploaded to any external server, cloud service, or remote location
• We have absolutely no access to your personal recipes, dietary preferences, or health data
• No network connection is required for recipe browsing, meal planning, or nutrition tracking

Local Storage:
• Your saved recipes, favorites, and collections are stored locally on your device
• Meal plans and shopping lists are kept entirely on your PC
• Custom recipes you create are stored only on your local machine
• You can export or delete any of your data at any time

Source Data Protection:
• The built-in recipe database is read-only and never modified
• Your personal data (favorites, meal plans, notes) is stored separately
• You maintain complete control over all your data at all times`
    },
    {
      title: '4. Data Storage and Security',
      content: `Local Data Storage:
All RecipeUp data is stored exclusively on your local device:
• Application preferences and settings
• Your saved recipes and favorites
• Meal plans and shopping lists
• Nutrition tracking history
• Custom recipes you create

Security Measures We Implement:
• The app follows Windows security best practices and guidelines
• We use industry-standard security frameworks for any data handling
• Regular security audits and code reviews
• Adherence to secure coding practices and industry standards
• Prompt security patches when vulnerabilities are discovered

Your Control Over Data:
• You can clear all app data through the Settings menu
• Export your recipes, meal plans, and other data anytime
• Uninstalling RecipeUp removes all associated local data
• No data persists on remote servers that you would need to request deletion of`
    },
    {
      title: '5. Third-Party Services',
      content: `RecipeUp is designed to operate primarily as an offline application with minimal third-party interactions:

Software Update Checks:
• We periodically check our secure servers for software updates
• Only anonymous technical data is transmitted: app version, Windows version, and processor type
• No personal information or recipe data is ever included in update checks
• You can disable automatic update checks in the app settings

Crash Reporting (Optional):
• If you opt-in to crash reporting, we use industry-standard crash analytics
• Crash reports contain technical information about the error, not your personal data
• Reports are anonymized and aggregated for analysis
• You can opt-out at any time through the app settings

We Do NOT:
• Sell your information to third parties under any circumstances
• Share your data with advertisers or marketing companies
• Use third-party tracking pixels or advertising SDKs
• Participate in data broker networks or exchanges
• Share your dietary, health, or nutrition data with anyone`
    },
    {
      title: '6. Your Rights and Choices',
      content: `You have comprehensive rights regarding your information and how RecipeUp operates:

Access and Control:
• Access all locally stored data through the app's Settings panel
• Export your recipes, meal plans, and preferences at any time
• Clear browsing history, cached data, and preferences
• Disable all optional data collection features

Opt-Out Options:
• Disable automatic update checks
• Opt-out of crash reporting
• Opt-out of anonymous analytics
• Disable all network communications (fully offline mode)

Data Deletion:
• Clear all app data through Settings → Privacy → Clear All Data
• Uninstalling RecipeUp removes all local data
• We retain no data on servers that would require deletion requests

For Users in the European Economic Area (EEA):
Under the General Data Protection Regulation (GDPR), you have additional rights including the right to access, rectification, erasure, restriction of processing, data portability, and the right to object.

For Users in California:
Under the California Consumer Privacy Act (CCPA), you have the right to know what personal information is collected, whether it is sold or disclosed, to say no to the sale of personal information, to access and request deletion of your personal information.`
    },
    {
      title: '7. Children\'s Privacy',
      content: `RecipeUp is not directed at children under the age of 13, and we do not knowingly collect personal information from children under 13.

If you are a parent or guardian and believe that your child has provided us with personal information without your consent, please contact us immediately at privacy@recipeup.com.

Upon verification, we will promptly delete any personal information from our records and take steps to ensure such collection does not occur in the future.`
    },
    {
      title: '8. International Data Transfers',
      content: `RecipeUp operates primarily as an offline application, meaning your recipes and personal data never leave your device. However, for the limited online features (such as update checks), data may be processed in different jurisdictions.

Our Commitments:
• Any international data transfers comply with applicable data protection laws
• We use Standard Contractual Clauses approved by the European Commission where required
• We ensure equivalent levels of data protection regardless of where data is processed
• Your data is always encrypted in transit using industry-standard TLS/SSL protocols`
    },
    {
      title: '9. Data Retention',
      content: `Retention Periods:

Local Data (on your device):
• Retained until you delete it or uninstall the application
• You have full control over retention through the app settings

Crash Reports (if opted-in):
• Retained for up to 90 days for analysis purposes
• Automatically purged after this period

Support Communications:
• Email correspondence is retained for up to 2 years to provide consistent support
• You can request deletion of support history at any time

We do not retain any data longer than necessary for the purposes described in this policy.`
    },
    {
      title: '10. Changes to This Privacy Policy',
      content: `We may update this Privacy Policy periodically to reflect changes in our practices, technology, legal requirements, or other factors.

When We Make Changes:
• We will update the "Last Updated" date at the top of this policy
• For significant changes, we will provide notice through the app
• Continued use of RecipeUp after changes constitutes acceptance

Previous versions of this policy are available upon request.`
    },
    {
      title: '11. Contact Us',
      content: `If you have any questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us:

Email: privacy@recipeup.com
General Support: support@recipeup.com
Website: https://recipeup.com

Response Times:
• We aim to respond to all privacy inquiries within 5 business days
• GDPR/CCPA requests will be addressed within 30 days as required by law

Last Updated: March 2026`
    }
  ];

  return (
    <div className="min-h-screen bg-[#0c0806] text-white">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-orange-600/10 rounded-full blur-[128px]" />
        <div className="absolute bottom-1/4 left-1/4 w-[400px] h-[400px] bg-amber-600/10 rounded-full blur-[128px]" />
      </div>

      <nav className="relative z-50 px-6 py-5 border-b border-white/5">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-11 h-11 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl flex items-center justify-center">
              <ChefHat className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>RecipeUp</span>
          </Link>
          <Link to="/" className="text-white/60 hover:text-white transition-colors font-medium flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </nav>

      <header className="relative z-10 py-16 px-6 border-b border-white/5">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-4 mb-6"
          >
            <div className="p-4 bg-gradient-to-br from-orange-500/20 to-amber-500/20 rounded-2xl border border-white/10">
              <Shield className="w-8 h-8 text-orange-400" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-bold">Privacy Policy</h1>
              <p className="text-white/50 mt-2">Your privacy matters. Here's how we protect it.</p>
            </div>
          </motion.div>
        </div>
      </header>

      <main className="relative z-10 py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="space-y-12"
          >
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ delay: index * 0.05 }}
                className="bg-white/[0.02] rounded-2xl border border-white/5 p-8"
              >
                <h2 className="text-2xl font-bold mb-4 text-white">{section.title}</h2>
                <div className="text-white/60 leading-relaxed whitespace-pre-line">
                  {section.content}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </main>

      <footer className="relative z-10 py-12 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-orange-500 to-amber-600 rounded-lg flex items-center justify-center">
              <ChefHat className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>RecipeUp</span>
          </div>
          <div className="flex gap-8 text-sm text-white/40">
            <Link to="/privacy" className="text-white">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
          </div>
          <p className="text-white/30 text-sm">© {new Date().getFullYear()} RecipeUp</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
